import {
  UserApi,
  User,
  OAuthTokenResponse,
  UserPurchases,
} from '@focusrite-novation/ampify-api';
import {OAuthOptions} from '../../../src/utils/auth';
import {AxiosResponse} from 'axios';
import {Items} from '../../../src/lib/purchases';

export enum PlanIDs {
  Equaliser = 'f18f37c5-deec-4722-a3b1-a681061f9bfb',
  Compressor = 'ea39d260-6bb8-4f1d-be08-58e8216bbc46',
  Reveal = '163dbcbd-5302-430e-a815-8cd757cbdb45',
  Limiter = '6be5053a-2117-4652-8dcb-9f430186eaf3',
  Balancer = 'f2fdb9c1-935a-4aaa-a4d6-886c11edf570',
  Verb = '1401ae3d-05cf-409d-b719-d88d58173ebc',
  Bundle4 = '47eb7fe6-c3f0-43f2-8f85-9752d5cf730d',
  Bundle5 = 'e2fd4a2a-8846-453d-af1d-902231900cbb',
}

export const oauthOptions: OAuthOptions = {
  client_id: 'snpm4ju3tf2m6p5p',
  scope: 'email profile openid offline_access',
};

export const fakeToken = (): OAuthTokenResponse => ({
  access_token: '',
  refresh_token: '',
  expires_in: 0,
  scope: '',
  token_type: '',
});

export const fakeUser = (): User => ({
  id: 123,
  firstName: 'Bob',
  lastName: 'Fossil',
  email: 'bob.fossil@zooniverse.com',
  plans: [],
  country: 'GB',
});

export const fakeUserWithFastEqualiser = (): User => ({
  id: 123,
  firstName: 'Bob',
  lastName: 'Fossil',
  email: 'bob.fossil@zooniverse.com',
  plans: [PlanIDs.Equaliser],
  country: 'GB',
});

export const fakeUserWithUnsupportedCountry = (): User => ({
  id: 123,
  firstName: 'Bob',
  lastName: 'Fossil',
  email: 'bob.fossil@zooniverse.com',
  plans: [],
  country: 'KZ',
});

export const fakeUserWithCustomPlans = (
  plans: PlanIDs[] = [PlanIDs.Reveal, PlanIDs.Compressor]
): User => {
  return {
    id: 123,
    firstName: 'Bob',
    lastName: 'Fossil',
    email: 'bob.fossil@zooniverse.com',
    plans,
    country: 'GB',
  };
};

export const fakeApi = (token?: OAuthTokenResponse, user?: User): UserApi => {
  return {
    getUser(options: any) {
      return Promise.resolve({
        data: user || fakeUser(),
      } as AxiosResponse<User>);
    },
    getAuthToken(options: any) {
      return Promise.resolve({
        data: token || fakeToken(),
      } as AxiosResponse<OAuthTokenResponse>);
    },
  } as UserApi;
};

export const validFakeToken = () => {
  const body = btoa(
    JSON.stringify({
      id: 'a6a173bd97013134c4b4310fdc708d1428fc7452',
      jti: 'a6a173bd97013134c4b4310fdc708d1428fc7452',
      iss: 'https://stgportal.ampifymusic.com/en',
      aud: 'snpm4ju3tf2m6p5p',
      sub: '2301987',
      exp: Math.floor(Date.now() / 100) + 3600,
      iat: 1602704450,
      token_type: 'bearer',
      scope: 'email profile openid offline_access',
    })
  );
  return {
    access_token: `eyJ0eXAiOiJKV1QiLCJhbGciOiJSUzI1NiJ9.${body}.Wa2B4i1G1FKoNqakPalt2Ce22zoIjTkqF3QPpS1PNJ1LvosRdEynk3WWSLqdnpTh1xjgMOIY9TO0VhllHvt7Q11Qbi1_vYCDRXDeSCrfb1HZ7mOoopg-dg6IeC5bn1h7_RV5oh1qV94VzXQduXqskr9g1NEw2L4am-XoQPjDThLyU6p58naISHcLaUoJZCnerq9i01szvSed3nCC4gQj0w0oNWcFNRTpPne1hSq8aHUFaUZ8tn0m5UnJjFEVazJAKgubG5xSAdSexRizJTEKNBQ1AkNtWHT-52OuEB9U71E7PXTlw9P-IR-QKdJkdwRzot0oYVGxuH3CX_ob1bJjaQ`,
    expires_in: 3600,
    token_type: 'bearer',
    scope: 'email profile openid offline_access',
    refresh_token: '656d3e30529b4bc75ce307c85d74140716c3ca1f',
    id_token:
      'eyJ0eXAiOiJKV1QiLCJhbGciOiJSUzI1NiJ9.eyJpc3MiOiJodHRwczpcL1wvc3RncG9ydGFsLWNvbGxlY3RpdmUuZm9jdXNyaXRlLmNvbVwvZW4iLCJzdWIiOiIyMzAxOTg3IiwiYXVkIjoic25wbTRqdTN0ZjJtNnA1cCIsImlhdCI6MTYwMjcwNDQ0OSwiZXhwIjoxNjAyNzA4MDQ5LCJhdXRoX3RpbWUiOjE2MDI3MDQ0NDl9.ryYQzDIawHPmMww2oJzPc2t4LGZH12e8rETCd_GYK1O721v39QBgE0PNO3vdv7-9vcLVhHq2HKpyqFZJqrMrxFa8gYd2zHR0F8YzXXAzImFyuw-zQwINam3GyIfs2dKVNND565YWTy1aiB-fsKxCiBWRg_qDAMr2BXptTgvarYFsprV0UuvUSZhWiCLq82tO2vYE0W99_Ake_oTvE8m5YlDJmdIHFZ2zLL8g7yHiZv7LX9xj50ZB_i-022Q8ClapuIRHRqgfYNubAVVUvaFz64lbMd08L6I_U9ZFoMFdLfqRouZ4Xc9xI4WmXLoiAEfhDy4fQeoO83H5bx8poZEY-w',
  };
};

export const fakePurchase = (
  itemId: Items = Items.FAST_COMPRESSOR
): UserPurchases => ({
  id: itemId as string,
  promotedFromRTO: false,
  createdOn: '2021-06-17T10:06:47.586Z',
});
